import { useEffect, useState } from 'react';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { useTranslation } from 'react-i18next';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from '../Organizations.module.css';
import { CloseOutlined } from '@ant-design/icons';
import {
  DarwinCol,
  DarwinDivider,
  DarwinRow,
  DarwinCard,
  SearchBox,
  DarwinSpin,
} from 'components/darwin-ui';
import { Button, Flex, Form } from 'antd';
import {
  useApproveDataSharingRequest,
  useGetOrganizationsByName,
} from 'api/services/organizationService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ErrorType } from 'types/genericTypes';

const { Text } = DarwinTypography;

type ApproveDataSharingModalProps = {
  open: boolean;
  onCancel: () => void;
  organizationName?: string;
  targetCompanyId: string;
  companyId: string;
  token: string;
};

export const ApproveDataSharingModal = ({
  open,
  onCancel,
  organizationName,
  targetCompanyId,
  companyId,
  token,
}: ApproveDataSharingModalProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { openNotification } = useFloatNotification();
  const {
    data: organizationByNameData,
    isFetching: isOrganizationByNameDataFetching,
    error: organizationByNameError,
  } = useGetOrganizationsByName({ name: searchValue });

  const { mutateAsync: approveDataSharingRequest } =
    useApproveDataSharingRequest();

  const handleMatchApproveDataSharing = async (
    isCreateAsNewOrganization: boolean
  ) => {
    // We validate that the data is fetched and its a valid data
    if (searchValue === '' && !isCreateAsNewOrganization) {
      openNotification(
        'Error',
        t('organization.approve-data-sharing.blank-search'),
        'error'
      );
      return;
    }
    if (
      organizationByNameData &&
      organizationByNameData.data.length === 0 &&
      !isCreateAsNewOrganization
    ) {
      openNotification(
        'Error',
        t('organization.approve-data-sharing.no-match-organization'),
        'error'
      );
      return;
    }

    try {
      const associatedCompanyId = isCreateAsNewOrganization
        ? ''
        : organizationByNameData?.data[0].id;
      await approveDataSharingRequest({
        companyId: companyId || '',
        targetCompanyId: targetCompanyId || '',
        token: token || '',
        associatedCompanyId,
      });
      openNotification(
        'Success',
        t('organization.approve-data-sharing.success-message'),
        'success'
      );
      onCancel();
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          'Error',
          t('organization.approve-data-sharing.error-approve-message'),
          'error'
        );
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    if (organizationByNameError && organizationByNameError.message) {
      openNotification(
        'Error',
        t('organization.approve-data-sharing.error-message'),
        'error'
      );
    }
  }, [organizationByNameError, openNotification, t]);

  return (
    <DarwinModal
      open={open}
      width={662}
      onCancel={onCancel}
      confirmLoading={false}
      centered
      footer={null}
      closeIcon={
        <CloseOutlined className={styles.dataSharingModalApproveCloseButton} />
      }
      title={
        <Text className={styles.dataSharingModalApproveTitle}>
          {t('organization.approve-data-sharing.title-text')}
        </Text>
      }
    >
      <Text className={styles.dataSharingModalApproveSecondTitle}>
        {t('organization.approve-data-sharing.title')}
      </Text>

      <DarwinDivider style={{ margin: '5px' }} />

      <Flex style={{ marginBottom: '1.5rem' }}>
        <Text>
          {t('organization.approve-data-sharing.paragraph')}{' '}
          <strong>
            {organizationName || '<Nombre de la organización solicitante>'}
          </strong>
          .
          <br />
          {t('organization.approve-data-sharing.paragraph2')}
        </Text>
      </Flex>

      <DarwinRow gutter={24}>
        <DarwinCol span={12}>
          {/* Firts Column */}
          <DarwinCard className={styles.dataSharingModalApproveCard}>
            <Flex
              vertical
              style={{
                height: '350px',
                justifyContent: 'space-between',
              }}
            >
              <Flex vertical>
                <Text className={styles.dataSharingModalApproveTitleCard}>
                  {t('organization.approve-data-sharing.title-text-card-one')}
                </Text>
                <Text className={styles.dataSharingModalApproveTextCard}>
                  {t('organization.approve-data-sharing.text-card-one', {
                    organizationName:
                      organizationName ||
                      '<Nombre de la organización solicitante>',
                  })}
                  <br />
                  {t('organization.approve-data-sharing.text-two-card-one')}
                </Text>
              </Flex>
              <Flex>
                <Button
                  type="primary"
                  className={styles.dataSharingModalButton}
                  onClick={() => handleMatchApproveDataSharing(true)}
                >
                  {t('organization.approve-data-sharing.button-card-one')}
                </Button>
              </Flex>
            </Flex>
          </DarwinCard>
        </DarwinCol>
        <DarwinCol span={12}>
          {/* Second Column */}
          <DarwinCard className={styles.dataSharingModalApproveCard}>
            <Flex
              vertical
              style={{
                height: '350px',
                justifyContent: 'space-between',
              }}
            >
              <Flex vertical>
                <Text className={styles.dataSharingModalApproveTitleCard}>
                  {t('organization.approve-data-sharing.title-text-card-two')}
                </Text>
                <SearchBox
                  placeholder={t(
                    'organization.approve-data-sharing.search-placeholder'
                  )}
                  onSearch={handleSearch}
                />
                {isOrganizationByNameDataFetching && (
                  <Flex
                    vertical
                    style={{
                      justifyContent: 'center',
                      alignContent: 'center',
                      height: '250px',
                    }}
                  >
                    <DarwinSpin />
                  </Flex>
                )}
                {!isOrganizationByNameDataFetching && searchValue === '' && (
                  <Text className={styles.dataSharingModalApproveTextCard}>
                    {t('organization.approve-data-sharing.text-card-two', {
                      organizationName:
                        organizationName ||
                        '<Nombre de la organización solicitante>',
                    })}
                    <br />
                    {t('organization.approve-data-sharing.text-two-card-two')}
                  </Text>
                )}

                {!isOrganizationByNameDataFetching &&
                  organizationByNameData?.data.length === 0 &&
                  searchValue !== '' && (
                    <Text className={styles.dataSharingModalApproveTextCard}>
                      {t('organization.approve-data-sharing.not-company-found')}
                    </Text>
                  )}

                {!isOrganizationByNameDataFetching &&
                  organizationByNameData &&
                  organizationByNameData?.data.length > 0 &&
                  searchValue !== '' && (
                    <Form layout="vertical">
                      <div
                        className={
                          styles.dataSharingModalApproveCardOrganizationExists
                        }
                      >
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganization
                          }
                        >
                          {t(
                            'organization.approve-data-sharing.primaryAddress'
                          )}
                        </Text>
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganizationExistsInformation
                          }
                        >
                          {
                            organizationByNameData.data[0].addresses[0]
                              .contacts[0].contactName
                          }
                        </Text>
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganization
                          }
                        >
                          {t('organization.approve-data-sharing.contactName')}
                        </Text>
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganizationExistsInformation
                          }
                        >
                          {
                            organizationByNameData.data[0].addresses[0]
                              .contacts[0].contactName
                          }
                        </Text>
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganization
                          }
                        >
                          {t('organization.approve-data-sharing.contactEmail')}
                        </Text>
                        <Text
                          className={
                            styles.dataSharingModalApproveCardOrganizationExistsInformation
                          }
                        >
                          {
                            organizationByNameData.data[0].addresses[0]
                              .contacts[0].contactEmail
                          }
                        </Text>
                      </div>
                    </Form>
                  )}
              </Flex>
              <Flex>
                <Button
                  type="primary"
                  className={styles.dataSharingModalButton}
                  onClick={() => handleMatchApproveDataSharing(false)}
                >
                  {t('organization.approve-data-sharing.button-card-two')}
                </Button>
              </Flex>
            </Flex>
          </DarwinCard>
        </DarwinCol>
      </DarwinRow>
    </DarwinModal>
  );
};
